import { styled, SxProps } from '@mui/material'
import { useIsDesktopSize } from '$common/react-hooks/layout'
import { useTextEditor } from './text-editor.hooks'
import { editorAPI } from './text-editor.api'
import { SendFieldAction } from '../../send-field.type'
import { putCaretAtEndOfEditor } from './ops'

const appDirection = 'rtl'

const EditorWrapper = styled('div')`
  display: flex;
  overflow: hidden;
  align-items: center;

  width: 100%;
  min-height: 49px;

  &[data-is-desktop='true'] {
    min-height: 57px;
  }

  position: relative;
  margin: 0 8px;
`

const Editor = styled('div')`
  width: 100%;
  max-height: 150px;
  min-height: 35px;
  color: #282828;
  caret-color: #55a6ff;
  background-color: transparent;
  unicode-bidi: plaintext;

  outline: none;
  overflow: hidden auto;
  position: relative;

  font-size: 14px;
  box-sizing: border-box;
  padding: 6px 3px 5px;
  line-height: 24px;
  font-weight: 400;

  white-space: pre-wrap;
  -webkit-user-modify: read-write-plaintext-only;

  &,
  & * {
    user-select: text;
  }

  & p {
    margin: 0;
    padding: 0;
  }
`

const PlaceHolder = styled('div')`
  width: 100%;
  height: 100%;
  padding: 6px 3px 5px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  font-weight: 500;
  font-size: 14px;

  color: #afafaf;
  position: absolute;
  pointer-events: none;
  top: 0;
`

export interface TextEditorPropTypes {
  placeholder: string
  darkMode: boolean
  action?: SendFieldAction
  mentionIsDisabled: boolean
  setHasMessage: (hasMessage: boolean) => unknown
  setMentionQuery: (query: string) => unknown
  onSubmit: () => unknown
  onPasteFile: (file: File) => unknown
  onContentChange: (text: string) => unknown
  sx?: SxProps
  className?: string
  editorId?: string
}

export function TextEditor({
  placeholder,
  darkMode,
  action,
  mentionIsDisabled,
  setHasMessage,
  setMentionQuery,
  onSubmit,
  onPasteFile,
  onContentChange,
  sx,
  className,
  editorId,
}: TextEditorPropTypes) {
  const isDesktopSize = useIsDesktopSize()

  const {
    EditorRef,
    editorIsEmpty,
    updateEditorState,
    onKeyDown,
    onCut,
    onCopy,
    onPaste,
  } = useTextEditor({
    action,
    mentionIsDisabled,
    setHasMessage,
    setMentionQuery,
    onSubmit,
    onPasteFile,
    onContentChange,
    editorId,
  })

  return (
    <EditorWrapper
      sx={sx}
      className={className}
      onClick={() => editorAPI.focus(editorId)}
      data-is-desktop={isDesktopSize}
    >
      <Editor
        ref={EditorRef}
        id={editorId}
        dir="auto"
        role="presentation"
        contentEditable
        suppressContentEditableWarning
        onInput={updateEditorState}
        onKeyDown={onKeyDown}
        onKeyUp={() => updateEditorState(false)}
        onFocus={() => {
          if (editorIsEmpty) putCaretAtEndOfEditor(EditorRef.current!)
          updateEditorState(false)
          editorAPI.setHasFocus(true, editorId)
        }}
        onBlur={() => editorAPI.setHasFocus(false, editorId)}
        onClick={() => updateEditorState(false)}
        onCut={onCut}
        onCopy={onCopy}
        onPaste={onPaste}
        sx={{
          color: darkMode ? 'white' : '#494949',
          textAlign: !editorIsEmpty
            ? 'initial'
            : appDirection === 'rtl'
              ? 'right'
              : 'left',
        }}
      />

      {editorIsEmpty && <PlaceHolder dir="auto">{placeholder}</PlaceHolder>}
    </EditorWrapper>
  )
}
